import Loadable from 'react-loadable';
import forIn from 'lodash/forIn';
import AuthenticatedRoute from '../components/layout/AuthenticatedLayout';
import UnauthenticatedRoute from '../components/layout/UnauthenticatedLayout';
import PublicRoute from '../components/layout/PublicLayout';
import NoMatch from '../components/page/NoMatch';
import FlashScreen from '../components/page/FlashScreen';
import uniqBy from 'lodash/uniqBy';
import warning from 'warning';
import Loading from '../components/layout/LoadingRoute';
import { USER_CONFIG } from './AppConfig';

const LoginPage = Loadable({
  loader: () => import('../views/auth/Login'),
  loading: Loading,
});

const ForgotPasswordPage = Loadable({
  loader: () => import('../views/auth/ForgotPassword'),
  loading: Loading,
});

const LoginStep2Page = Loadable({
  loader: () => import('../views/auth/LoginStep2'),
  loading: Loading,
});

const ResetPasswordPage = Loadable({
  loader: () => import('../views/auth/ResetPassword'),
  loading: Loading,
});

const AdminCompanyPage = Loadable({
  loader: () => import('../views/admin/Company'),
  loading: Loading,
});

const AdminCompanyDetailPage = Loadable({
  loader: () => import('../views/admin/CompanyDetail'),
  loading: Loading,
});

const AdminCompanyCreatePage = Loadable({
  loader: () => import('../views/admin/CompanyCreate'),
  loading: Loading,
});

const AdminAccountSettingPage = Loadable({
  loader: () => import('../views/admin/AccountSetting'),
  loading: Loading,
});

const CompanyDashboardPage = Loadable({
  loader: () => import('../views/company/Dashboard'),
  loading: Loading,
});

const CompanyDataPage = Loadable({
  loader: () => import('../views/company/Data'),
  loading: Loading,
});

const CompanyDataDetailPage = Loadable({
  loader: () => import('../views/company/DataDetail'),
  loading: Loading,
});

const CompanyImportCSVPage = Loadable({
  loader: () => import('../views/company/ImportCSV'),
  loading: Loading,
});

const CompanyMessagePage = Loadable({
  loader: () => import('../views/company/Message'),
  loading: Loading,
});

const CompanyAccountSettingPage = Loadable({
  loader: () => import('../views/company/AccountSetting'),
  loading: Loading,
});

const companyHelpPage = Loadable({
  loader: () => import('../views/company/HelpPage'),
  loading: Loading,
});

// Public Pages
const DisclosureTopPage = Loadable({
  loader: () => import('../views/end-user/DisclosureTop'),
  loading: Loading,
});

const SectionDetailPage = Loadable({
  loader: () => import('../views/end-user/SectionDetail'),
  loading: Loading,
});

const PatientGroupTopPage = Loadable({
  loader: () => import('../views/end-user/PatientGroupTop'),
  loading: Loading,
});

const PatientSectionDetailPage = Loadable({
  loader: () => import('../views/end-user/PatientSectionDetail'),
  loading: Loading,
});

const TermOfUsagePage = Loadable({
  loader: () => import('../views/end-user/TermOfUsage'),
  loading: Loading,
});

const SearchPage = Loadable({
  loader: () => import('../views/end-user/Search'),
  loading: Loading,
});

const FAQPage = Loadable({
  loader: () => import('../views/end-user/FAQ'),
  loading: Loading,
});

const ViewerPage = Loadable({
  loader: () => import('../views/end-user/Viewer'),
  loading: Loading,
});

const PreviewPage = Loadable({
  loader: () => import('../views/company/Preview'),
  loading: Loading,
});

export const NoMatchPage = NoMatch;
export const FlashScreenPage = FlashScreen;

const routes = {
  authorizedRoutes: {
    name: 'authenticated', // use to define id when create routes
    layout: AuthenticatedRoute,
    routes: [
      {
        path: '/admin/profile',
        name: 'menu.admin.profile',
        title: 'label.profile',
        menuIconClass: 'business',
        component: AdminAccountSettingPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.SYSTEM_ADMIN.roleLevel]
      },
      {
        path: '/admin/companies',
        name: 'menu.company',
        title: 'menu.company',
        menuIconClass: 'business',
        component: AdminCompanyPage,
        showInMenu: true,
        accessRole: [USER_CONFIG.SYSTEM_ADMIN.roleLevel]
      },
      {
        path: '/admin/companies/detail/:id',
        name: 'menu.company.detail',
        title: 'title.companyDetail',
        menuIconClass: 'business',
        component: AdminCompanyDetailPage,
        showInMenu: false,
        accessRole: [
            USER_CONFIG.SYSTEM_ADMIN.roleLevel,
          // USER_CONFIG.COMPANY_ADMIN.roleLevel
        ]
      },
      {
        path: '/admin/companies/list/create',
        name: 'menu.company.create',
        title: 'title.addCompany',
        menuIconClass: 'business',
        component: AdminCompanyCreatePage,
        showInMenu: false,
        accessRole: [USER_CONFIG.SYSTEM_ADMIN.roleLevel]
      },
      {
        path: '/company/dashboard',
        name: 'menu.dashboard',
        title: 'menu.dashboard',
        menuIconClass: 'dashboard',
        component: CompanyDashboardPage,
        showInMenu: true,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/data',
        name: 'menu.data',
        title: 'menu.data',
        menuIconClass: 'storage',
        component: CompanyDataPage,
        showInMenu: true,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/data/list/:type',
        name: 'menu.data',
        title: 'menu.data',
        menuIconClass: 'storage',
        component: CompanyDataPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/data/disclosure/:id',
        name: 'menu.data',
        title: 'menu.dataDetail',
        menuIconClass: 'storage',
        component: CompanyDataDetailPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/data/patient/:id',
        name: 'menu.data',
        title: 'menu.dataDetail',
        menuIconClass: 'storage',
        component: CompanyDataDetailPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/data/import-csv',
        name: 'menu.import.csv',
        title: 'label.importCSV',
        menuIconClass: 'storage',
        component: CompanyImportCSVPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/messages',
        name: 'menu.messages',
        title: 'menu.messages',
        menuIconClass: 'note',
        component: CompanyMessagePage,
        showInMenu: true,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/messages/:message',
        name: 'menu.messages',
        title: 'menu.messages',
        menuIconClass: 'note',
        component: CompanyMessagePage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/profile',
        name: 'menu.accountSetting',
        title: 'label.accountSetting',
        menuIconClass: 'person',
        component: CompanyAccountSettingPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/help',
        name: 'menu.helpPage',
        title: 'label.help',
        menuIconClass: 'help',
        component: companyHelpPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/help/:number',
        name: 'menu.helpPage',
        title: 'label.help',
        menuIconClass: 'help',
        component: companyHelpPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/help/:number/:subNumber',
        name: 'menu.helpPage',
        title: 'label.help',
        menuIconClass: 'help',
        component: companyHelpPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/company/preview',
        name: 'menu.preview',
        title: 'label.preview',
        menuIconClass: 'present_to_all',
        component: PreviewPage,
        showInMenu: true,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      // Preview Routes
      {
        path: '/preview/disclosure',
        name: 'menu.disclosureTop',
        title: 'title.disclosure',
        component: DisclosureTopPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/disclosure/detail/:id',
        name: 'menu.disclosure.detail',
        title: 'title.sectionDetail',
        component: SectionDetailPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/patient',
        name: 'menu.patient',
        title: 'title.patient',
        component: PatientGroupTopPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/patient/detail/:id',
        name: 'menu.patient.detail',
        title: 'title.sectionDetail',
        component: PatientSectionDetailPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/search',
        name: 'menu.search',
        title: 'label.search',
        component: SearchPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview',
        name: 'menu.termOfUsage',
        title: 'title.termOfUsage',
        component: TermOfUsagePage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/faq',
        name: 'menu.faq',
        title: 'title.faqPage',
        component: FAQPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/faq/topics/:number',
        name: 'menu.faq',
        title: 'title.faqPage',
        component: FAQPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/faq/topics/:number/:subNumber',
        name: 'menu.faq',
        title: 'title.faqPage',
        component: FAQPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      },
      {
        path: '/preview/viewer',
        name: 'menu.viewer',
        title: '',
        component: ViewerPage,
        showInMenu: false,
        accessRole: [USER_CONFIG.COMPANY_ADMIN.roleLevel]
      }
    ],
  },
  unauthorizedRoutes: {
    name: 'unauthenticated',
    layout: UnauthenticatedRoute,
    routes: [
      {
        path: '/login',
        title: 'menu.login',
        component: LoginPage,
      },
      {
        path: '/forget-password',
        title: 'label.forgotPassword',
        component: ForgotPasswordPage,
      },
      {
        path: '/login-step-2',
        title: 'menu.loginStep2',
        component: LoginStep2Page,
      },
      {
        path: '/reset-password/:type',
        title: 'button.resetPassword',
        component: ResetPasswordPage,
      },
    ],
  },
  publicRoutes: {
    name: 'public',
    layout: PublicRoute,
    routes: [
      {
        path: '/disclosure',
        name: 'menu.disclosureTop',
        title: 'title.disclosure',
        component: DisclosureTopPage
      },
      {
        path: '/disclosure/detail/:id',
        name: 'menu.disclosure.detail',
        title: 'title.sectionDetail',
        component: SectionDetailPage
      },
      {
        path: '/patient',
        name: 'menu.patient',
        title: 'title.patient',
        component: PatientGroupTopPage
      },
      {
        path: '/patient/detail/:id',
        name: 'menu.patient.detail',
        title: 'title.sectionDetail',
        component: PatientSectionDetailPage
      },
      {
        path: '/search',
        name: 'menu.search',
        title: 'label.search',
        component: SearchPage
      },
      {
        path: '/',
        name: 'menu.termOfUsage',
        title: 'title.termOfUsage',
        component: TermOfUsagePage
      },
      {
        path: '/faq',
        name: 'menu.faq',
        title: 'title.faqPage',
        component: FAQPage
      },
      {
        path: '/faq/topics/:number',
        name: 'menu.faq',
        title: 'title.faqPage',
        component: FAQPage
      },
      {
        path: '/faq/topics/:number/:subNumber',
        name: 'menu.faq',
        title: 'title.faqPage',
        component: FAQPage
      },
      {
        path: '/viewer',
        name: 'menu.viewer',
        title: '',
        component: ViewerPage
      }
    ]
  }
};

function _createRoutes(routes = [], path = '', layout, id) {
  let flattenedRoutes = [];
  routes.forEach(route => {
    const {
      path: subPath,
      layout: subLayout,
      exact,
      routes: subRoutes,
      component,
      relativePath,
      redirect,
      ...rest
    } = route;
    if (!!component) {
      flattenedRoutes.push(
        Object.assign(
          {
            layoutId: id, // use to compare layout when change route
            layout: subLayout || layout,
            path: path + subPath,
            component: component,
            ...rest,
          },
          subRoutes &&
            !!subRoutes[0] && {
              exact: false,
              routes: _createRoutes(
                subRoutes,
                path + subPath,
                subLayout || layout,
                id
              ),
            },
          !subRoutes && {
            exact: exact || true,
          },
          redirect && {
            redirect: relativePath ? redirect : path + subPath + redirect,
          }
        )
      );
    } else {
      warning(
        false,
        "Missing route's component, config will be ignore %s",
        route.path
      );
    }
  });

  return flattenedRoutes;
}

export default function createRoutes() {
  let flattenedRoutes = [];
  let i = 0;
  forIn(routes, ({ name = '', layout, routes: layoutRoutes }) => {
    layoutRoutes &&
      flattenedRoutes.push(
        ..._createRoutes(layoutRoutes, '', layout, `${name}_${i++}`)
      );
  });

  // push no match route
  const filteredDuplicate = uniqBy(flattenedRoutes, 'path');
  warning(
    filteredDuplicate.length === flattenedRoutes.length,
    'There are duplicate path in routes config, only first config are accepted all another config will be ignored'
  );

  return filteredDuplicate;
}

export function flattenRoutes(routes = [], init = []) {
  return routes.reduce((accumulator, { routes, ...rest }) => {
    accumulator.push(rest);
    routes && routes.length > 0 && flattenRoutes(routes, accumulator);
    return accumulator;
  }, init);
}

function _filterMenuRoutes(routes = [], path = '') {
  let menu = [];
  if (routes) {
    routes.forEach(route => {
      if (route.showInMenu) {
        let _route = {};
        if (route.routes) {
          let sub = _filterMenuRoutes(route.routes, path + route.path);
          sub.length > 1 && (_route.routes = sub);
        }

        menu.push(
          Object.assign(
            {},
            {
              path: path + route.path,
              name: route.name || '',
              title: route.title || '',
              exact: route.exact || true,
              iconClass: route.menuIconClass,
              accessRole: route.accessRole || []
            },
            _route
          )
        );
      }
    });
  }

  return menu;
}

export function filterMenuRoutes() {
  let menuRoutes = [];
  forIn(routes, ({ layout, routes }) => {
    routes && menuRoutes.push(..._filterMenuRoutes(routes));
  });

  return menuRoutes;
}

export { routes };
